import React, { useCallback } from 'react';
import { useActiveSubstanceFilters } from '@client/feature/activeSubstance/hooks/useActiveSubstanceFilters';
import { useActiveSubstances } from '@client/feature/activeSubstance/hooks/useActiveSubstance';
import Result from '@client/feature/activeSubstance/components/Search/Result/Result';
import { FilterPanels, BackgroundSection } from './partials';
import { useTranslation } from 'react-i18next';
import { Spin } from 'antd';
import { usePageMeta } from '@client/hooks/usePageMeta';
import SearchTopBackground from '../../../img/active-substance-bg-top.png';


/**
 * Main search page component for active substances with filters and results
 * Supports cold start from shared URLs with ID-based filters
 * @returns {JSX.Element} The active substance search page
 */
const ActiveSubstanceSearchPage = () => {
  const {
    filters,
    filterLabels,
    updateFilterWithLabels,
    updatePagination,
    updateSorting,
    getCurrentSorting,
    resetFilters,
    hasActiveFilters,
    getFilterLabel,
    isLoadingLabels,
    enrichedData,
    getLabelsForField,
  } = useActiveSubstanceFilters();

  const { data, isLoading, error } = useActiveSubstances(filters);
  const { t } = useTranslation();
  usePageMeta({
    title: t('seo|activeSubstance.searchPageTitle'),
    description: t('seo|activeSubstance.searchPageDescription'),
    breadcrumbTitle: t('breadcrumb|activeSubstance.searchTitle'),
    breadcrumbDescription: t('breadcrumb|activeSubstance.searchDescription'),
  });
  const handlePageChange = useCallback(
    ({ page, size }) => {
      updatePagination({ page, size });
    },
    [updatePagination]
  );
  const handleSorting = useCallback(
    (field, order) => updateSorting(field, order),
    [updateSorting]
  );
  const handleReset = useCallback(() => resetFilters(), [resetFilters]);
  const handleRemoveFilter = useCallback(
    (filterKey, valueId) => {
      const currentValues = filters[filterKey] || [];
      const newValues = currentValues.filter((v) => v !== valueId);
      updateFilterWithLabels(filterKey, newValues);
    },
    [filters, updateFilterWithLabels]
  );

  // Show loading spinner during cold start label resolution
  if (isLoadingLabels) {
    return (
      <div className="relative w-full min-h-screen">
        <div className="container">
          <BackgroundSection backgroundSource={SearchTopBackground} showBottomBg={false} />
          <div className="flex justify-center items-center py-20">
            <Spin size="large" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full min-h-screen">
      <div className="container">
        <BackgroundSection backgroundSource={SearchTopBackground} showBottomBg={false} />
        <div className="flex flex-col gap-8 relative z-10">
          <div className="filters-container">
            <FilterPanels
              filters={filters}
              onFilterChange={updateFilterWithLabels}
              filterLabels={filterLabels}
              onReset={handleReset}
            />
          </div>

          <Result
            filters={filters}
            data={data}
            isLoading={isLoading}
            error={error}
            getFilterLabel={getFilterLabel}
            onResetFilters={handleReset}
            onRemoveFilter={handleRemoveFilter}
            onPageChange={handlePageChange}
            onSortChange={handleSorting}
            currentSorting={getCurrentSorting()}
          />
        </div>
        {!isLoading && (
          <>
            <p>{t('active-substance|table.bottomText1')}</p>
            <p className="mb-4">{t('active-substance|table.bottomText2')}</p>
          </>
        )}
      </div>
    </div>
  );
};

export default ActiveSubstanceSearchPage;
